import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Microscope } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { useContent } from "../App";

const SECTIONS = [
    {
        title: "What you're actually smelling",
        body: "Bad breath has a chemistry set behind it: volatile sulfur compounds — mostly hydrogen sulfide and methyl mercaptan — released when anaerobic bacteria break down proteins from food, dead cells and mucus. The human nose detects some of these compounds at astonishingly low concentrations, which is why even a small bacterial colony can produce a noticeable smell.",
    },
    {
        title: "The tongue is a bacterial landscape",
        body: "The surface of the tongue is covered in tiny projections called papillae, which multiply its surface area enormously. Towards the back of the tongue, oxygen is scarce — exactly the environment anaerobic, sulfur-producing bacteria prefer. The white or yellow coating many people notice is a mix of those bacteria, shed cells and food debris. It's the reason clinical studies consistently find the tongue to be the dominant source of oral malodour — and why a ten-second scrape outperforms an extra minute of brushing.",
    },
    {
        title: "Why dry mouth changes everything",
        body: "Saliva is not just moisture: it rinses away debris, buffers acids and carries oxygen that keeps anaerobic bacteria in check. Saliva flow drops dramatically during sleep, which is why morning breath is universal. Anything that dries the mouth — mouth breathing, dehydration, caffeine, alcohol, and many common medications — tilts the balance towards odour. Hydration is unglamorous, but it is one of the most evidence-aligned fixes there is.",
    },
    {
        title: "Gums and the hiding places",
        body: "When plaque sits along the gumline, the gum can pull away slightly and form shallow pockets — low-oxygen hideouts a toothbrush cannot reach. Bleeding when brushing is often the first visible sign of this inflammation. Interdental cleaning (floss or a water flosser) is the only daily habit that reaches these spaces, which is why dentists insist on it.",
    },
    {
        title: "What measuring devices actually tell you",
        body: "Clinical halimeters quantify volatile sulfur compounds in parts per billion and are genuinely precise — that's why they're used in research. Home smart monitors use similar sensing principles at consumer prices; they won't match a lab instrument, but they don't need to. The trend is what matters: watching your score fall over two weeks of better habits is far more useful than any single reading. Budget keychain testers, meanwhile, are best treated as a rough fresh-or-not signal.",
    },
    {
        title: "The routine the evidence points to",
        body: "Strip away the marketing and the research-aligned routine is short: mechanically clean the tongue every morning, keep saliva flowing (water, sugar-free gum, nasal breathing), clean between teeth every night, and if you rinse, choose an alcohol-free formula with zinc — zinc ions bind directly to sulfur compounds and neutralise them, while alcohol-based rinses dry the mouth and can backfire over time.",
    },
    {
        title: "When it's not just your mouth",
        body: "Roughly one in ten persistent cases isn't primarily oral: chronic sinus issues, tonsil stones and reflux are the usual suspects, with rarer metabolic causes behind them. The practical rule: if two to three weeks of a solid routine changes nothing, see a dentist first — they can rule out gum and tongue causes quickly and point you onwards if needed.",
    },
];

const GuidePage = () => {
    const content = useContent();
    return (
        <div data-testid="guide-page" className="min-h-screen bg-[#FDFCF8]">
            <Header />
            <main className="max-w-[760px] mx-auto px-4 sm:px-6 py-14 sm:py-20">
                <Reveal>
                    <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] font-bold text-[#718370]">
                        <span className="h-px w-10 bg-[#9AB098]" />
                        Deep-dive guide
                    </p>
                </Reveal>
                <Reveal delay={0.08}>
                    <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1D1A] leading-[1.1]">
                        The Science Behind Bad Breath
                    </h1>
                </Reveal>
                <Reveal delay={0.16}>
                    <p className="mt-5 text-sm sm:text-base leading-relaxed text-[#6D736D]">
                        What researchers actually know about halitosis — sulfur chemistry, tongue biofilm, saliva dynamics — and how it maps to the simple habits that fix most cases. Written to be cited, shared and linked.
                    </p>
                </Reveal>

                <div className="mt-12">
                    {SECTIONS.map((s, i) => (
                        <Reveal key={i} delay={0.05}>
                            <section className="group grid grid-cols-[48px_1fr] sm:grid-cols-[72px_1fr] gap-4 sm:gap-6 border-t border-[#E4DFD3] py-9 first:border-t-0">
                                <div className="font-serif text-3xl sm:text-5xl font-bold leading-none text-[#DDE5DB] transition-colors duration-500 group-hover:text-[#9AB098] select-none">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <div>
                                    <h2 className="font-serif text-xl sm:text-2xl font-semibold text-[#1A1D1A]">{s.title}</h2>
                                    <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#6D736D]">{s.body}</p>
                                </div>
                            </section>
                        </Reveal>
                    ))}
                </div>

                <Reveal>
                    <div className="grain relative mt-6 overflow-hidden rounded-[20px] bg-[#222522] px-7 py-10 sm:px-10 text-center">
                        <Microscope size={26} strokeWidth={1.5} className="mx-auto text-[#9AB098]" />
                        <h2 className="mt-4 font-serif text-2xl font-semibold text-[#FDFCF8]">See how your own routine scores</h2>
                        <p className="mx-auto mt-3 max-w-[420px] text-sm leading-relaxed text-[#FDFCF8]/70">
                            Eight questions, sixty seconds — our free Breath Score Calculator turns this science into your personal starting point.
                        </p>
                        <Link
                            to="/tools/breath-score"
                            data-testid="guide-cta-score"
                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#9AB098] px-7 py-3 text-sm font-bold text-[#222522] transition-colors hover:bg-[#DDE5DB]"
                        >
                            Take the 60-second check <ArrowRight size={15} strokeWidth={2} />
                        </Link>
                    </div>
                </Reveal>

                <Link to="/tools" className="mt-8 inline-flex items-center gap-1.5 text-xs font-medium text-[#6D736D] transition-colors hover:text-[#1A1D1A]">
                    <ArrowLeft size={13} /> All free tools
                </Link>
            </main>
            {content && <Footer footer={content.content.footer} />}
        </div>
    );
};

export default GuidePage;
